import { RightOutlined } from "@ant-design/icons";
import { Collapse } from "antd";

import {
    formatMlbTeamLabel,
    formatMlbNextGameWhenCompact,
    mlbTeamLogoImgProps,
} from "../utils/mlb-display";

const { Panel } = Collapse;

/**
 * Team row card (standings expand + draft) — all viewports.
 *
 * @param {object} props
 * @param {Record<string, unknown>} props.record — team row (key, city, name, logo, wins, …)
 * @param {string|null|undefined} props.metaPrefix — e.g. "Pick #4" or "#4 · Owner"
 */
export const MlbTeamMobileCard = ({ record, metaPrefix }) => {
    const rec = null; // Disabled — last10 scores unreliable from API
    const run = null;
    const games = Array.isArray(record.next_games) ? record.next_games : [];
    const last10Games = [];
    const hasFun = Boolean(games.length);

    return (
        <article className="wl-mlb-team-card">
            <div className="wl-mlb-team-card__top">
                <img
                    alt=""
                    {...mlbTeamLogoImgProps(record.key, record.logo)}
                    className="team_logo wl-mlb-team-card__logo"
                />
                <div className="wl-mlb-team-card__main">
                    {metaPrefix ? (
                        <div className="wl-mlb-team-card__meta">{metaPrefix}</div>
                    ) : null}
                    <div className="wl-mlb-team-card__name">
                        {formatMlbTeamLabel(record)}
                    </div>
                    <div className="wl-mlb-team-card__record">
                        <span className="wl-mlb-team-card__wl">
                            {record.ties != null && record.ties !== ""
                                ? `${record.wins}-${record.losses}-${record.ties}`
                                : `${record.wins}-${record.losses}`}
                        </span>
                        <span className="wl-mlb-team-card__sep" aria-hidden="true">
                            ·
                        </span>
                        <span className="wl-mlb-team-card__pct">
                            {record.percentage}
                        </span>
                    </div>
                </div>
            </div>

            {hasFun ? (
                <div className="wl-mlb-team-card__body">
                    {rec || run || last10Games.length ? (
                        <div className="wl-mlb-team-card__section">
                            <div className="wl-mlb-team-card__label">Last 10</div>
                            {last10Games.length ? (
                                <>
                                    {rec ? (
                                        <div className="wl-mlb-team-card__l10-head">
                                            <span className="wl-mlb-team-card__l10-rec">
                                                {rec}
                                            </span>
                                        </div>
                                    ) : null}
                                    <ul className="wl-mlb-team-card__l10-list">
                                        {last10Games.map((g, i) => {
                                            const letter = String(
                                                g.result || ""
                                            ).toUpperCase();
                                            const resultMod =
                                                letter === "W"
                                                    ? "wl-mlb-team-card__l10-result--w"
                                                    : letter === "L"
                                                      ? "wl-mlb-team-card__l10-result--l"
                                                      : "wl-mlb-team-card__l10-result--t";
                                            const resultWord =
                                                letter === "W"
                                                    ? "Win"
                                                    : letter === "L"
                                                      ? "Loss"
                                                      : "Tie";
                                            return (
                                                <li
                                                    key={i}
                                                    className="wl-mlb-team-card__l10-item"
                                                >
                                                    <div className="wl-mlb-team-card__l10-primary">
                                                        <img
                                                            alt={
                                                                g.opp_label ||
                                                                g.line ||
                                                                ""
                                                            }
                                                            {...mlbTeamLogoImgProps(
                                                                g.opp_key,
                                                                g.opp_logo
                                                            )}
                                                            className="team_logo wl-mlb-team-card__l10-logo"
                                                        />
                                                        <div className="wl-mlb-team-card__l10-copy">
                                                            <div className="wl-mlb-team-card__l10-row1">
                                                                <span
                                                                    className={`wl-mlb-team-card__l10-result ${resultMod}`}
                                                                    aria-label={
                                                                        resultWord
                                                                    }
                                                                >
                                                                    {letter}
                                                                </span>
                                                                {g.score ? (
                                                                    <span className="wl-mlb-team-card__l10-score">
                                                                        {
                                                                            g.score
                                                                        }
                                                                    </span>
                                                                ) : null}
                                                                {g.line ? (
                                                                    <span className="wl-mlb-team-card__l10-line">
                                                                        {
                                                                            g.line
                                                                        }
                                                                    </span>
                                                                ) : null}
                                                            </div>
                                                            {g.date ? (
                                                                <span className="wl-mlb-team-card__l10-date">
                                                                    {g.date}
                                                                </span>
                                                            ) : null}
                                                            {g.opp_owner ? (
                                                                <span className="wl-mlb-team-card__l10-owner">
                                                                    <span className="wl-mlb-team-card__l10-owner-label">
                                                                        Owner
                                                                    </span>
                                                                    {
                                                                        g.opp_owner
                                                                    }
                                                                </span>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </>
                            ) : (
                                <div className="wl-mlb-team-card__l10">
                                    {rec ? (
                                        <span className="wl-mlb-team-card__l10-rec">
                                            {rec}
                                        </span>
                                    ) : null}
                                    {run ? (
                                        <span
                                            className="wl-mlb-team-card__l10-run"
                                            aria-label={`Results sequence: ${run}`}
                                        >
                                            {run.split("").join("\u00a0")}
                                        </span>
                                    ) : null}
                                </div>
                            )}
                        </div>
                    ) : null}

                    {games.length ? (
                        <div className="wl-mlb-team-card__section wl-mlb-team-card__section--upcoming">
                            <Collapse
                                bordered={false}
                                ghost
                                className="wl-mlb-upcoming-collapse"
                                defaultActiveKey={[]}
                                expandIconPosition="right"
                                expandIcon={({ isActive }) => (
                                    <RightOutlined
                                        className="wl-mlb-upcoming-collapse__chev"
                                        rotate={isActive ? 90 : 0}
                                    />
                                )}
                            >
                                <Panel
                                    header={
                                        <span className="wl-mlb-upcoming-collapse__header">
                                            Upcoming{" "}
                                            <span className="wl-mlb-upcoming-collapse__count">
                                                ({games.length})
                                            </span>
                                        </span>
                                    }
                                    key="upcoming"
                                >
                                    <ul className="wl-mlb-team-card__next-list">
                                        {games.map((ng, i) => (
                                            <li
                                                key={i}
                                                className="wl-mlb-team-card__next-item"
                                            >
                                                <div className="wl-mlb-team-card__next-primary">
                                                    <img
                                                        alt={
                                                            ng.opp_label ||
                                                            ng.line ||
                                                            ""
                                                        }
                                                        {...mlbTeamLogoImgProps(
                                                            ng.opp_key,
                                                            ng.opp_logo
                                                        )}
                                                        className="team_logo wl-mlb-team-card__next-logo"
                                                    />
                                                    <div className="wl-mlb-team-card__next-copy">
                                                        <span className="wl-mlb-team-card__next-line">
                                                            {ng.line}
                                                        </span>
                                                        {ng.opp_owner ? (
                                                            <span className="wl-mlb-team-card__next-owner">
                                                                <span className="wl-mlb-team-card__next-owner-label">
                                                                    Owner
                                                                </span>
                                                                {ng.opp_owner}
                                                            </span>
                                                        ) : null}
                                                        <span className="wl-mlb-team-card__next-when">
                                                            {formatMlbNextGameWhenCompact(
                                                                ng
                                                            ) ||
                                                                ng.when ||
                                                                ""}
                                                        </span>
                                                    </div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </Panel>
                            </Collapse>
                        </div>
                    ) : null}
                </div>
            ) : null}
        </article>
    );
};

const Guy_Table = (props) => {
    const sortPick = (a, b) => {
        const pa =
            a.draft_order == null || a.draft_order === ""
                ? 9999
                : Number(a.draft_order);
        const pb =
            b.draft_order == null || b.draft_order === ""
                ? 9999
                : Number(b.draft_order);
        if (pa !== pb) {
            return pa - pb;
        }
        return String(a.key).localeCompare(String(b.key));
    };

    const data = Object.entries(props.team_data)
        .map((item) => {
            return {
                key: item[1].key,
                draft_order: item[1].draft_order,
                city: item[1].city,
                name: item[1].name,
                logo: item[1].logo,
                wins: item[1].wins,
                losses: item[1].losses,
                percentage: item[1].percentage,
                last10_record: item[1].last10_record,
                last10_run: item[1].last10_run,
                last10_games: item[1].last10_games,
                next_games: item[1].next_games,
            };
        })
        .sort(sortPick);

    return (
        <div className="wl-mlb-team-card-stack">
            {data.map((row) => {
                const pickLabel =
                    row.draft_order != null && row.draft_order !== ""
                        ? `Pick #${row.draft_order}`
                        : null;
                return (
                    <MlbTeamMobileCard
                        key={`${row.draft_order}-${row.key}`}
                        record={row}
                        metaPrefix={pickLabel}
                    />
                );
            })}
        </div>
    );
};

export default Guy_Table;
